import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useDomainServices } from "@/providers/DomainServices";

import {
  AgentThreadStatus,
  type AgentThreadRecord,
} from "../AgentThreadRecord";

import { activeAgentThreadQueryKeyPrefix } from "./useActiveAgentThread";

interface RetryFailedTurnParams {
  threadId: string;
  orgId: string;
}

interface RetryFailedTurnContext {
  previousAgentThreads: Array<
    [ReadonlyArray<unknown>, AgentThreadRecord | undefined]
  >;
}

/**
 * Runs a thread's last turn again after it failed.
 *
 * The thread shows as working as soon as the request goes out, so the chat
 * polls for the new reply. A refused request puts the thread back as it was.
 * Either way the thread is then read again, so the chat shows what the server
 * did: when another retry of the same turn got there first, that retry shows
 * as running.
 */
export function useRetryFailedTurn() {
  const { agentThreadService } = useDomainServices();
  const queryClient = useQueryClient();

  return useMutation<
    void,
    Error,
    RetryFailedTurnParams,
    RetryFailedTurnContext
  >({
    mutationFn: ({ threadId, orgId }) =>
      agentThreadService.retryFailedTurn(threadId, { orgId }),
    onMutate: async ({ threadId }) => {
      const queryKey = activeAgentThreadQueryKeyPrefix(threadId);
      await queryClient.cancelQueries({ queryKey });
      const previousAgentThreads =
        queryClient.getQueriesData<AgentThreadRecord>({ queryKey });
      queryClient.setQueriesData<AgentThreadRecord>(
        { queryKey },
        (agentThread) =>
          agentThread === undefined
            ? agentThread
            : { ...agentThread, status: AgentThreadStatus.ROBOTO_TURN },
      );
      return { previousAgentThreads };
    },
    onError: (_error, _variables, context) => {
      context?.previousAgentThreads.forEach(([queryKey, agentThread]) => {
        queryClient.setQueryData(queryKey, agentThread);
      });
    },
    onSettled: (_data, _error, { threadId }) =>
      queryClient.refetchQueries({
        queryKey: activeAgentThreadQueryKeyPrefix(threadId),
      }),
  });
}
